import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, ExternalLink, Calendar, Clock, Award, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { PageSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FilmSection = ({ film, index, isReversed }) => {
  // Determine the link path
  let filmPath = `/films/${film.id}`;
  if (film.id === 'older-gods') filmPath = '/films/older-gods';
  if (film.id === 'patriarch') filmPath = '/films/patriarch';
  
  const isComingSoon = film.status === 'in_production' || film.year > new Date().getFullYear() || (film.platforms && film.platforms.length === 0);
  const isOlderGods = film.id === 'older-gods';
  const isPatriarch = film.id === 'patriarch';
  
  return (
    <section 
      className={`py-16 md:py-24 ${index % 2 === 0 ? 'bg-neutral-950' : 'bg-neutral-900/30'}`}
      data-testid={`film-section-${film.id}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
          {/* Poster */}
          <motion.div
            initial={{ opacity: 0, x: isReversed ? 20 : -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className={isReversed ? 'lg:order-2' : 'lg:order-1'}
          >
            <Link to={filmPath} className="block relative group">
              <img
                src={film.poster_url}
                alt={film.title}
                className="w-full max-w-md mx-auto lg:mx-0 aspect-[2/3] object-cover shadow-2xl transition-transform duration-500 group-hover:scale-[1.02]"
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="bg-orange-600 text-white font-bold py-3 px-6 uppercase tracking-widest text-sm">
                  {isComingSoon ? 'LEARN MORE' : 'VIEW DETAILS'}
                </span>
              </div>
              {/* Badge */}
              {isComingSoon && (
                <div className="absolute top-4 right-4">
                  <span className="bg-white text-black text-xs font-bold px-3 py-1.5 uppercase tracking-wider">
                    COMING SOON
                  </span>
                </div>
              )}
              {film.is_flagship && !isComingSoon && (
                <div className="absolute top-4 right-4">
                  <span className="bg-orange-600 text-white text-xs font-bold px-3 py-1.5 uppercase tracking-wider">
                    NOW STREAMING
                  </span>
                </div>
              )}
            </Link>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: isReversed ? -20 : 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className={isReversed ? 'lg:order-1' : 'lg:order-2'}
          >
            {/* Status/Genre badges */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {isComingSoon ? (
                <span className="bg-orange-600 text-white text-xs font-bold px-3 py-1.5 uppercase tracking-wider">
                  COMING {film.release_date || film.year}
                </span>
              ) : (
                <span className="bg-orange-600/20 border border-orange-500/30 text-orange-400 text-xs font-bold px-3 py-1.5 uppercase tracking-wider">
                  NOW AVAILABLE
                </span>
              )}
              <span className="bg-white/10 border border-white/20 text-white text-xs font-bold px-3 py-1.5 uppercase tracking-wider">
                {film.genre}
              </span>
            </div>

            {/* Title */}
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight uppercase text-white mb-4">
              {film.title}
            </h2>

            {/* Tagline */}
            {film.tagline && (
              <p className="text-orange-500 text-lg md:text-xl font-medium italic mb-4">
                {film.tagline}
              </p>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-neutral-400 text-sm mb-6">
              <span className="flex items-center gap-1.5">
                <Calendar size={16} className="text-orange-500" />
                {film.year}
              </span>
              {film.runtime && film.runtime !== 'TBC' && (
                <span className="flex items-center gap-1.5">
                  <Clock size={16} className="text-orange-500" />
                  {film.runtime}
                </span>
              )}
              <span>Dir. {film.director}</span>
              {film.production_company && (
                <span>{film.production_company}</span>
              )}
            </div>

            {/* Synopsis */}
            <p className="text-neutral-300 text-lg leading-relaxed mb-6">
              {film.synopsis}
            </p>

            {/* Awards */}
            {film.awards && film.awards.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {film.awards.slice(0, 2).map((award, i) => (
                  <span key={i} className="flex items-center gap-2 bg-neutral-900 border border-white/10 px-3 py-2 text-sm">
                    <Award size={14} className="text-orange-500" />
                    <span className="text-neutral-300">{award}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Platforms (for released films) */}
            {!isComingSoon && film.platforms && film.platforms.length > 0 && (
              <div className="mb-8">
                <span className="text-neutral-500 text-xs uppercase tracking-wider block mb-3">Available on</span>
                <div className="flex flex-wrap gap-3">
                  {film.platforms.map((platform) => (
                    <span key={platform} className="text-white/70 font-display text-sm uppercase tracking-wider">
                      {platform}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-4">
              {/* Trailer button (if available) */}
              {film.trailer_url && (
                <a
                  href={film.trailer_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-bold py-3 px-6 uppercase tracking-widest text-sm transition-all"
                >
                  <Play size={18} fill="white" />
                  WATCH TRAILER
                </a>
              )}
              
              {/* Learn more / View details button */}
              <Link
                to={filmPath}
                className="flex items-center gap-2 border border-white/20 hover:border-white text-white font-bold py-3 px-6 uppercase tracking-widest text-sm transition-all"
              >
                {isComingSoon ? 'LEARN MORE' : 'VIEW DETAILS'}
                <ArrowRight size={18} />
              </Link>

              {/* Watch now button for released films */}
              {!isComingSoon && film.platform_links?.amazon_us && (
                <a
                  href={film.platform_links.amazon_us}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-white hover:bg-neutral-200 text-black font-bold py-3 px-6 uppercase tracking-widest text-sm transition-all"
                >
                  WATCH NOW
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default function Films() {
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/films`);
        // Sort films: Older Gods first, then Patriarch, then others
        const sortedFilms = response.data.sort((a, b) => {
          if (a.id === 'older-gods') return -1;
          if (b.id === 'older-gods') return 1;
          if (a.id === 'patriarch') return -1;
          if (b.id === 'patriarch') return 1;
          return 0;
        });
        setFilms(sortedFilms);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="films-page" className="min-h-screen pt-20 bg-neutral-950">
      <PageSEO page="films" />
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1920&q=80"
            alt="Cinema film reel - browse our distributed films catalogue UK"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/90 to-neutral-950/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4">
              OUR CATALOGUE
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight uppercase leading-none text-white mb-6">
              FILMS
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              From award-winning releases to exciting upcoming productions, explore our growing catalogue of films and documentaries.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Films List */}
      {loading ? (
        <section className="py-24 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <div className="w-12 h-12 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        </section>
      ) : (
        <>
          {films.map((film, index) => (
            <FilmSection 
              key={film.id} 
              film={film} 
              index={index}
              isReversed={index % 2 === 1}
            />
          ))}
        </>
      )}

      {/* CTA */}
      <section className="py-24 md:py-32 bg-neutral-950 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-6">
              GOT A FILM?
            </h2>
            <p className="text-neutral-400 text-lg leading-relaxed mb-8">
              We're always looking for compelling films and documentaries to add to our catalogue. Submit your completed project today.
            </p>
            <Link
              to="/submit"
              className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-10 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
            >
              SUBMIT YOUR FILM
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
